import { useEffect, useState, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Zap, Home as HomeIcon, Menu, X, ListMusic, FolderOpen, User, LogIn, Download, Mail, CheckCircle, BookOpen, Disc3, Sparkles, Monitor } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import ServiceWorkerUpdater from "@/components/ServiceWorkerUpdater";
import WhatsNewModal from "@/components/WhatsNewModal";
import GlobalAudioPlayer from "@/components/GlobalAudioPlayer";
import AnalyticsPage from "@/pages/AnalyticsPage";
import BillingPage from "@/pages/BillingPage";
import BrandLogo from "@/components/BrandLogo";

// Pages
import HomePage from "@/pages/HomePage";
import LibraryPage from "@/pages/LibraryPage";
import TrackDetailPage from "@/pages/TrackDetailPage";
import PlaylistsPage from "@/pages/PlaylistsPage";
import PlaylistDetailPage from "@/pages/PlaylistDetailPage";
import CratesPage from "@/pages/CratesPage";
import CrateDetailPage from "@/pages/CrateDetailPage";
import SignupPage from "@/pages/SignupPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminPage from "@/pages/AdminPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import DocsPage from "@/pages/DocsPage";
import AnalyzerLandingPage from "@/pages/AnalyzerLandingPage";
import AdminDesktopPage from "@/pages/AdminDesktopPage";
import MixesPage from "@/pages/MixesPage";
import SupportPage from "@/pages/SupportPage";
import TicketPortalPage from "@/pages/TicketPortalPage";
import MyTicketsPage from "@/pages/MyTicketsPage";
import SetsPage from "@/pages/SetsPage";
import AssistantPage from "@/pages/AssistantPage";
import DashboardPage from "@/pages/DashboardPage";
import DashboardShell from "@/components/DashboardShell";
import UploadPage from "@/pages/UploadPage";
import LoginPage from "@/pages/LoginPage";
import LoginPopover from "@/components/LoginPopover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// App context
const AppContext = createContext(null);
export const useAppContext = () => useContext(AppContext);

// Protected Route component - requires authentication
const EmailVerificationGate = ({ email }) => {
  const { logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(email)}`);
      setSent(true);
    } catch {
      // silent fail
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center"
      >
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm">
          <div className="w-16 h-16 bg-amber-50 border border-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="font-heading text-xl text-[#0F172A] mb-2" data-testid="verify-gate-title">Verify Your Email</h2>
          <p className="font-body text-[#64748B] text-sm mb-2">
            We sent a verification link to:
          </p>
          <p className="font-mono text-[#0F172A] mb-4" data-testid="verify-gate-email">{email}</p>
          <p className="font-body text-[#64748B] text-xs mb-6">
            Please check your inbox and click the link to activate your account. Check spam if you don't see it.
          </p>

          {sent ? (
            <div className="flex items-center justify-center gap-2 text-green-400 mb-4" data-testid="verify-gate-sent">
              <CheckCircle className="w-4 h-4" />
              <span className="font-body text-sm">Verification email sent!</span>
            </div>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              className="w-full mb-4 py-3 border border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A] hover:border-[#2563FF]/50 transition-colors font-heading text-sm"
              data-testid="verify-gate-resend"
            >
              {resending ? (
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto" />
              ) : (
                'RESEND VERIFICATION EMAIL'
              )}
            </button>
          )}

          <button
            onClick={logout}
            className="font-body text-[#64748B] hover:text-[#0F172A] text-sm transition-colors"
            data-testid="verify-gate-logout"
          >
            Sign out and use a different account
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const ProtectedRoute = ({ children, adminOnly = false, userOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/?login=1" replace />;
  }
  
  // If email not verified, show verification screen (skip for admins)
  if (user?.role !== 'admin' && user?.email_verified === false) {
    return <EmailVerificationGate email={user.email} />;
  }
  
  // If route is admin-only and user is not admin, redirect to home
  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  // If route is user-only (Library, Crates, etc.) and user is admin, redirect to admin dashboard
  if (userOnly && user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Navigation component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  // Public nav links (only for logged out users)
  const publicLinks = [
    { path: '/', label: 'Home', icon: HomeIcon },
    { path: '/analyzer', label: 'Analyzer', icon: Monitor },
    { path: '/docs', label: 'Docs', icon: BookOpen },
  ];
  
  // Protected nav links for regular users (only visible when logged in and NOT admin)
  const userLinks = [
    { path: '/library', label: 'Library', icon: Music },
    { path: '/crates', label: 'Crates', icon: FolderOpen },
    { path: '/playlists', label: 'Playlists', icon: ListMusic },
    { path: '/assistant', label: 'AI Assistant', icon: Sparkles },
    { path: '/mixes', label: 'Mixes', icon: Disc3 },
    { path: '/analyzer', label: 'Analyzer', icon: Monitor },
    { path: '/docs', label: 'Docs', icon: BookOpen },
  ];
  
  // Determine which nav links to show
  let navLinks = [];
  if (!isAuthenticated) {
    // Not logged in - show Home only
    navLinks = publicLinks;
  } else if (user?.role === 'admin') {
    // Admin - no nav links (just profile/admin in the auth section)
    navLinks = [];
  } else {
    // Regular users - show Library, Crates, Playlists, Upload (no Home)
    navLinks = userLinks;
  }

  // Hide top nav for any in-shell authenticated route (sidebar + rail take over).
  const SHELL_PREFIXES = ['/dashboard','/library','/crates','/crate','/playlists','/playlist','/mixes','/assistant','/sets','/track','/profile','/upload','/analytics','/billing'];
  if (SHELL_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return null;
  }

  return (
    <>
    <nav className={`fixed top-0 left-0 right-0 z-50 border-b border-[#E2E8F0] ${isOpen ? 'bg-[#F5F7FB]' : 'bg-white/90 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-[84px]">
          {/* Logo — box icon + Nasalization wordmark */}
          <Link to={isAuthenticated && user?.role !== 'admin' ? '/dashboard' : '/'} className="flex items-center group" data-testid="brand-logo">
            <BrandLogo size="md" className="transition-transform group-hover:scale-[1.02] [&>img]:w-11 [&>img]:h-11 sm:[&>img]:w-14 sm:[&>img]:h-14 [&>span:last-child]:text-[18px] sm:[&>span:last-child]:text-[22px]" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`relative flex items-center gap-1.5 px-2.5 lg:px-4 py-2 font-heading text-xs lg:text-sm uppercase tracking-wider transition-colors duration-200 ${
                  location.pathname === link.path || 
                  (link.path !== '/' && location.pathname.startsWith(link.path))
                    ? 'text-[#2563FF] bg-[#EFF6FF]'
                    : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
                }`}
              >
                <link.icon className="w-4 h-4" />
                <span>{link.label}</span>
              </Link>
            ))}
            
            {/* Auth links */}
            <div className="ml-2 pl-2 lg:ml-4 lg:pl-4 border-l border-[#E2E8F0] flex items-center gap-1">
              {isAuthenticated ? (
                <Link
                  to="/profile"
                  data-testid="nav-profile"
                  className={`flex items-center gap-1.5 px-2.5 lg:px-4 py-2 font-heading text-xs lg:text-sm uppercase tracking-wider transition-colors duration-200 ${
                    location.pathname === '/profile' || location.pathname === '/admin'
                      ? 'text-[#2563FF] bg-[#EFF6FF]'
                      : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span className="hidden lg:inline">{user?.name?.split(' ')[0] || 'Profile'}</span>
                </Link>
              ) : (
                <LoginPopover />
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
            className="md:hidden p-2 text-[#64748B] hover:text-[#0F172A]"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav - Full screen overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] md:hidden bg-[#F5F7FB] flex flex-col"
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          <div className="flex justify-end p-5">
            <button
              onClick={() => setIsOpen(false)}
              className="p-3 text-[#64748B] hover:text-[#0F172A]"
              data-testid="mobile-menu-close"
            >
              <X className="w-7 h-7" />
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-8 py-4 font-heading text-xl uppercase tracking-widest transition-colors ${
                  location.pathname === link.path ? 'text-[#2563FF]' : 'text-[#64748B] hover:text-[#0F172A]'
                }`}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}

            <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
              {isAuthenticated ? (
                <Link to="/profile" onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-8 py-4 font-heading text-xl uppercase tracking-widest text-[#64748B] hover:text-[#0F172A] transition-colors">
                  <User className="w-5 h-5" /> Profile
                </Link>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-8 py-4 font-heading text-xl uppercase tracking-widest text-[#64748B] hover:text-[#0F172A] transition-colors">
                  <LogIn className="w-5 h-5" /> Login
                </Link>
              )}
            </div>
          </div>

          <div className="p-6 flex justify-center">
            <BrandLogo size="sm" className="opacity-60" />
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

function AppMain() {
  const location = useLocation();
  const SHELL_PATHS = ['/dashboard','/library','/crates','/crate','/playlists','/playlist','/mixes','/assistant','/sets','/track','/profile','/upload','/analytics','/billing'];
  const inShell = SHELL_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/') || location.pathname.startsWith(p + '?')) || location.pathname.startsWith('/dashboard');
  if (inShell) {
    return (
      <DashboardShell embeddedHeader={location.pathname !== '/dashboard'}>
        <Routes>
          <Route path="/dashboard" element={<ProtectedRoute userOnly><DashboardPage /></ProtectedRoute>} />
          <Route path="/library" element={<ProtectedRoute userOnly><LibraryPage /></ProtectedRoute>} />
          <Route path="/track/:id" element={<ProtectedRoute userOnly><TrackDetailPage /></ProtectedRoute>} />
          <Route path="/crates" element={<ProtectedRoute userOnly><CratesPage /></ProtectedRoute>} />
          <Route path="/crate/:id" element={<ProtectedRoute userOnly><CrateDetailPage /></ProtectedRoute>} />
          <Route path="/playlists" element={<ProtectedRoute userOnly><PlaylistsPage /></ProtectedRoute>} />
          <Route path="/mixes" element={<ProtectedRoute userOnly><MixesPage /></ProtectedRoute>} />
          <Route path="/playlist/:id" element={<ProtectedRoute userOnly><PlaylistDetailPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/tickets" element={<ProtectedRoute><MyTicketsPage /></ProtectedRoute>} />
          <Route path="/upload" element={<ProtectedRoute userOnly><UploadPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute userOnly><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute userOnly><BillingPage /></ProtectedRoute>} />
          <Route path="/assistant" element={<ProtectedRoute userOnly><AssistantPage /></ProtectedRoute>} />
          <Route path="/sets" element={<ProtectedRoute userOnly><SetsPage /></ProtectedRoute>} />
        </Routes>
      </DashboardShell>
    );
  }
  return (
    <main className="pt-16 sm:pt-[84px]">
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/analyzer" element={<AnalyzerLandingPage />} />
        <Route path="/admin/desktop" element={<ProtectedRoute><AdminDesktopPage /></ProtectedRoute>} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/ticket/:id" element={<TicketPortalPage />} />
        {/* Admin-only route */}
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      </Routes>
    </main>
  );
}

function App() {
  const [trackCount, setTrackCount] = useState(0);
  const [playlistCount, setPlaylistCount] = useState(0);

  const refreshCounts = async () => {
    try {
      const [tracksRes, playlistsRes] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/playlists`)
      ]);
      setTrackCount(tracksRes.data.tracks?.length || 0);
      setPlaylistCount(playlistsRes.data.playlists?.length || 0);
    } catch (e) {
      console.error('Failed to fetch counts');
    }
  };

  // Note: no mount-time refreshCounts() — it would run before auth is set,
  // wasting a 306 KB /tracks response on a 401. Pages that need the counts
  // call refreshCounts() explicitly after each mutation (see LibraryPage,
  // PlaylistsPage, SetsPage). Meanwhile DashboardPage falls back to
  // libCtx.tracks_count from /dj/library-context which loads lazily anyway.

  return (
    <AuthProvider>
      <PlayerProvider>
      <AppContext.Provider value={{ trackCount, playlistCount, refreshCounts }}>
        <div className="min-h-screen bg-[#F5F7FB]">
          <BrowserRouter>
            <Navigation />
            <AppMain />
            <Toaster 
              position="bottom-right"
              toastOptions={{
                style: {
                  background: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  color: '#0F172A',
                  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
                  borderRadius: '12px',
                },
              }}
            />
            <ServiceWorkerUpdater />
            <WhatsNewModal />
            <GlobalAudioPlayer />
          </BrowserRouter>
        </div>
      </AppContext.Provider>
      </PlayerProvider>
    </AuthProvider>
  );
}

export default App;
